import { ComponentProps, createContext, useContext, useRef } from "react";
import { Transition, TransitionStatus } from "react-transition-group";
import { twMerge } from "tailwind-merge";
import { ToggleProps, TransitionClasses } from "../types";
import Card from "./Card";
import Portal from "./Portal";

type Context = {
  isOpen: boolean;
  toggle: VoidFunction;
  transitionState: TransitionStatus;
  duration: number;
};
type ModalProps = { duration?: number } & ToggleProps;
type ModalDialogProps = Omit<ComponentProps<"div">, "as" | "ref">;
type ModalHeaderProps = ComponentProps<"div">;
type ModalBodyProps = ComponentProps<"div">;
type ModalFooterProps = ComponentProps<"div">;

export const ModalContext = createContext<Context>({
  isOpen: false,
  toggle: () => {},
  transitionState: "unmounted",
  duration: 300,
});

function Modal({
  isOpen = false,
  toggle = () => {},
  duration = 300,
  className,
  children,
  ...props
}: ModalProps & Omit<ComponentProps<"div">, keyof ModalProps>) {
  const divRef = useRef<HTMLDivElement | null>(null);
  const Component = "div";
  const transitionClasses: TransitionClasses = {
    entering: "active opacity-100 pointer-events-auto",
    entered: "active opacity-100 pointer-events-auto",
    exiting: "opacity-0 pointer-events-none",
    exited: "opacity-0 pointer-events-none",
    unmounted: "",
  };
  return (
    <Portal>
      <Transition nodeRef={divRef} in={isOpen} timeout={duration} unmountOnExit>
        {(state) => (
          <Component
            ref={divRef}
            data-open={isOpen}
            style={{ transitionDuration: `${duration}ms` }}
            className={twMerge(
              "modal fixed size-full inset-0 flex flex-col z-10 bg-transparent transition-opacity p-4",
              transitionClasses[state],
              className
            )}
            {...props}
          >
            <button
              type="button"
              onClick={() => toggle()}
              className="absolute inset-0 cursor-default opacity-0"
            />
            <ModalContext.Provider
              value={{ isOpen, toggle, transitionState: state, duration }}
            >
              {children}
            </ModalContext.Provider>
          </Component>
        )}
      </Transition>
    </Portal>
  );
}
function ModalDialog({ className, children, ...props }: ModalDialogProps) {
  const { transitionState, duration } = useContext(ModalContext);
  const transitionClasses: TransitionClasses = {
    entering: "translate-y-0",
    entered: "translate-y-0",
    exiting: "translate-y-full",
    exited: "translate-y-full",
    unmounted: "",
  };
  const modalSize = '("min-[576px]:w-[500px]"';
  return (
    <Card
      style={{ transitionDuration: `${duration}ms` }}
      className={twMerge(
        "max-w-full max-h-full mx-auto mt-auto transition-[transform] shadow-2xl",
        modalSize,
        transitionClasses[transitionState],
        className
      )}
      {...props}
    >
      {children}
    </Card>
  );
}
function ModalHeader({ className, ...props }: ModalHeaderProps) {
  return <Card.Header className={className} {...props} />;
}
function ModalBody({ className, ...props }: ModalBodyProps) {
  return <Card.Body className={className} {...props} />;
}
function ModalFooter({ className, ...props }: ModalFooterProps) {
  return <Card.Footer className={className} {...props} />;
}

Modal.Dialog = ModalDialog;
Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;

export default Modal;
